/**
 * Permission catalog (CLAUDE.md §3.2, §7). One permission = one action. Synced into the
 * `permissions` table on every deploy; roles group permissions and the owner can change role
 * permissions later without code. Later phases add their own permissions here.
 *
 * - `sensitive`: shown with a warning in the roles screen; changes to it are highlighted in audit.
 * - `offlineCapable`: may ever be used in an offline session (the actual allow-list per role and
 *   device location type is in OFFLINE_POLICY_DEFAULTS and editable by the owner — D16 / O3).
 * - `segregated`: held by one role on purpose (separation of duties), so holding it does not make
 *   someone more senior; ignored by the "nobody acts on someone more powerful" check (D45).
 */
export interface PermissionDefinition {
  readonly code: string;
  readonly module: string;
  readonly descriptionAr: string;
  readonly descriptionEn: string;
  readonly sensitive?: boolean;
  readonly offlineCapable?: boolean;
  readonly segregated?: boolean;
}

export const PERMISSIONS = [
  // Organization
  {
    code: 'location.view',
    module: 'org',
    descriptionAr: 'عرض الفروع والمواقع',
    descriptionEn: 'View branches and locations',
  },
  {
    code: 'location.manage',
    module: 'org',
    descriptionAr: 'إضافة وتعديل الفروع والمواقع',
    descriptionEn: 'Add and edit branches and locations',
    sensitive: true,
  },
  // Settings
  {
    code: 'settings.view',
    module: 'settings',
    descriptionAr: 'عرض الإعدادات',
    descriptionEn: 'View settings',
  },
  {
    code: 'settings.operations.edit',
    module: 'settings',
    descriptionAr: 'تعديل إعدادات التشغيل',
    descriptionEn: 'Edit operations settings',
  },
  {
    code: 'settings.finance.edit',
    module: 'settings',
    descriptionAr: 'تعديل الإعدادات المالية',
    descriptionEn: 'Edit finance settings',
    sensitive: true,
  },
  {
    code: 'settings.security.edit',
    module: 'settings',
    descriptionAr: 'تعديل إعدادات الأمان',
    descriptionEn: 'Edit security settings',
    sensitive: true,
  },
  // Users and roles
  {
    code: 'user.view',
    module: 'users',
    descriptionAr: 'عرض المستخدمين',
    descriptionEn: 'View users',
  },
  {
    code: 'user.create',
    module: 'users',
    descriptionAr: 'إضافة مستخدم',
    descriptionEn: 'Create users',
  },
  {
    code: 'user.update',
    module: 'users',
    descriptionAr: 'تعديل بيانات مستخدم',
    descriptionEn: 'Edit users',
  },
  {
    code: 'user.suspend',
    module: 'users',
    descriptionAr: 'إيقاف أو إعادة تفعيل مستخدم',
    descriptionEn: 'Suspend or reactivate users',
    sensitive: true,
  },
  {
    code: 'user.reset_credentials',
    module: 'users',
    descriptionAr: 'إعادة تعيين كلمة المرور أو الرمز أو التحقق بخطوتين لمستخدم',
    descriptionEn: "Reset a user's password, PIN or 2FA",
    sensitive: true,
  },
  {
    code: 'user.assign_roles',
    module: 'users',
    descriptionAr: 'منح الأدوار للمستخدمين',
    descriptionEn: 'Assign roles to users',
    sensitive: true,
  },
  {
    code: 'role.view',
    module: 'users',
    descriptionAr: 'عرض الأدوار والصلاحيات',
    descriptionEn: 'View roles and permissions',
  },
  {
    code: 'role.manage',
    module: 'users',
    descriptionAr: 'تعديل صلاحيات الأدوار',
    descriptionEn: 'Change role permissions',
    sensitive: true,
  },
  // Devices and sessions
  {
    code: 'device.view',
    module: 'devices',
    descriptionAr: 'عرض الأجهزة',
    descriptionEn: 'View devices',
  },
  {
    code: 'device.enroll',
    module: 'devices',
    descriptionAr: 'تسجيل جهاز جديد',
    descriptionEn: 'Enroll new devices',
    sensitive: true,
  },
  {
    code: 'device.revoke',
    module: 'devices',
    descriptionAr: 'إلغاء جهاز',
    descriptionEn: 'Revoke devices',
    sensitive: true,
  },
  {
    // Owner rule O27 (M7-5): on a branch PC's "Connection to the main PC" page, pairing, typing or
    // changing the hub address and "find automatically". Checked by Dob Desktop's main process.
    // Offline-capable, so a branch manager or IT signed in without internet can fix a connection.
    code: 'hub.connection.manage',
    module: 'devices',
    descriptionAr: 'تغيير اتصال الجهاز بالجهاز الرئيسي (الإقران والعنوان)',
    descriptionEn: "Change a PC's connection to its main PC (pairing and address)",
    offlineCapable: true,
  },
  {
    // M7-6 (plan §11): add, edit and remove a location's network printers, in the admin web and
    // in Dob Desktop. Offline-capable, so a branch can fix a printer's address without internet.
    code: 'printer.manage',
    module: 'devices',
    descriptionAr: 'إضافة وتعديل وحذف طابعات الموقع',
    descriptionEn: "Add, edit and remove a location's printers",
    offlineCapable: true,
  },
  {
    code: 'session.view_all',
    module: 'devices',
    descriptionAr: 'عرض جلسات جميع المستخدمين',
    descriptionEn: "View all users' sessions",
  },
  {
    code: 'session.revoke_any',
    module: 'devices',
    descriptionAr: 'إنهاء جلسة أي مستخدم',
    descriptionEn: "Revoke any user's session",
    sensitive: true,
  },
  // Audit, emergency, approvals
  {
    code: 'audit.view',
    module: 'audit',
    descriptionAr: 'عرض سجل التدقيق',
    descriptionEn: 'View the audit log',
  },
  {
    code: 'audit.verify',
    module: 'audit',
    descriptionAr: 'فحص سلامة سجل التدقيق',
    descriptionEn: 'Verify audit log integrity',
  },
  {
    code: 'emergency.activate',
    module: 'emergency',
    descriptionAr: 'تفعيل وضع الطوارئ',
    descriptionEn: 'Activate Emergency Mode',
    sensitive: true,
    // IT's break-glass; partners deliberately do not hold it, yet are senior to IT.
    segregated: true,
  },
  {
    code: 'emergency.end',
    module: 'emergency',
    descriptionAr: 'إنهاء وضع الطوارئ',
    descriptionEn: 'End Emergency Mode',
  },
  {
    code: 'approval.view',
    module: 'approvals',
    descriptionAr: 'عرض طلبات الموافقة',
    descriptionEn: 'View approval requests',
  },
  {
    code: 'approval.decide.finance',
    module: 'approvals',
    descriptionAr: 'الموافقة على الطلبات المالية الحساسة',
    descriptionEn: 'Decide sensitive financial requests',
    sensitive: true,
  },
  // Ledger and cash
  {
    code: 'ledger.view',
    module: 'ledger',
    descriptionAr: 'عرض الدفتر المحاسبي',
    descriptionEn: 'View the ledger',
  },
  {
    code: 'ledger.period.close',
    module: 'ledger',
    descriptionAr: 'إغلاق فترة محاسبية',
    descriptionEn: 'Close an accounting period',
    sensitive: true,
  },
  {
    code: 'ledger.correction.request',
    module: 'ledger',
    descriptionAr: 'طلب تصحيح قيد أو إعادة فتح فترة',
    descriptionEn: 'Request an entry correction or period reopen',
    sensitive: true,
  },
  {
    code: 'cashbox.view',
    module: 'cash',
    descriptionAr: 'عرض الصناديق النقدية',
    descriptionEn: 'View cash boxes',
  },
  {
    code: 'cashbox.manage',
    module: 'cash',
    descriptionAr: 'إنشاء الصناديق النقدية وتغيير المسؤول عنها وحالتها',
    descriptionEn: 'Create cash boxes and change their holder or state',
    sensitive: true,
  },
  {
    code: 'cashbox.transfer.send',
    module: 'cash',
    descriptionAr: 'تسليم نقد إلى صندوق آخر',
    descriptionEn: 'Hand over cash to another box',
    offlineCapable: true,
  },
  {
    code: 'cashbox.transfer.receive',
    module: 'cash',
    descriptionAr: 'تأكيد استلام نقد',
    descriptionEn: 'Confirm receiving cash',
    offlineCapable: true,
  },
  {
    code: 'cashbox.transfer.dispute',
    module: 'cash',
    descriptionAr: 'الاعتراض على مبلغ مستلم',
    descriptionEn: 'Dispute a received amount',
    offlineCapable: true,
  },
  // Sync, notifications, backups
  {
    code: 'sync.issue.view',
    module: 'sync',
    descriptionAr: 'عرض مشاكل المزامنة',
    descriptionEn: 'View sync issues',
    offlineCapable: true,
  },
  {
    code: 'sync.issue.resolve',
    module: 'sync',
    descriptionAr: 'حل مشاكل المزامنة',
    descriptionEn: 'Resolve sync issues',
  },
  {
    code: 'sync.issue.approve',
    module: 'sync',
    descriptionAr: 'الموافقة على حل مشاكل المزامنة المالية',
    descriptionEn: 'Approve the resolution of financial sync issues',
    sensitive: true,
  },
  {
    code: 'notification.view_log',
    module: 'notifications',
    descriptionAr: 'عرض سجل الرسائل',
    descriptionEn: 'View the message log',
  },
  {
    code: 'notification.send_test',
    module: 'notifications',
    descriptionAr: 'إرسال رسالة تجريبية',
    descriptionEn: 'Send a test message',
  },
  {
    code: 'backup.view',
    module: 'backups',
    descriptionAr: 'عرض حالة النسخ الاحتياطي',
    descriptionEn: 'View backup status',
  },
  {
    // M7-8: what the office Backup Agent refused on its own (a mass deletion on the server, a
    // changed repository config) goes ahead only when a partner says so. The database also checks
    // that the person is a partner (0016), whoever holds this permission.
    code: 'backup.office_copy.release',
    module: 'backups',
    descriptionAr: 'السماح لنسخة المكتب بما رفضته (حذف جماعي متوقَّع، تغيّر إعداد المستودع)',
    descriptionEn:
      'Let the office copy go ahead with what it refused (an expected mass deletion, a changed repository config)',
    sensitive: true,
    segregated: true,
  },
  // ── Phase 2 — warehouse (docs/plans/PHASE_2.md §18, D243). Added together in W1 so roles can be
  // set up once; each one starts to matter in the step that builds its screens.
  // Inventory
  {
    code: 'inventory.view',
    module: 'inventory',
    descriptionAr: 'عرض المواد والكميات والدفعات وحركات المخزون (بدون التكاليف)',
    descriptionEn: 'View items, quantities, batches and stock movements (without costs)',
    offlineCapable: true,
  },
  {
    code: 'inventory.cost.view',
    module: 'inventory',
    descriptionAr: 'عرض التكاليف وقيم المخزون والهوامش وقيد كل مستند',
    descriptionEn: "View costs, stock values, margins and each document's journal entry",
    // Not sensitive, like ledger.view: it shows numbers and gives no power over people or money.
    // Marked sensitive, it would have stopped IT from resetting the general manager's password
    // (D45 ranking; found by the D51 test in CI, W1).
  },
  {
    code: 'inventory.item.manage',
    module: 'inventory',
    descriptionAr: 'إضافة وتعديل المواد والأصناف والوحدات والحد الأدنى',
    descriptionEn: 'Add and edit items, categories, units and minimum levels',
  },
  {
    code: 'inventory.price.manage',
    module: 'inventory',
    descriptionAr: 'تحديد أسعار التحويل للفروع',
    descriptionEn: 'Set transfer prices to branches',
    sensitive: true,
  },
  {
    code: 'inventory.waste.record',
    module: 'inventory',
    descriptionAr: 'تسجيل التالف مع صورة',
    descriptionEn: 'Record waste with a photo',
    offlineCapable: true,
  },
  {
    code: 'inventory.fefo.override',
    module: 'inventory',
    descriptionAr: 'اختيار دفعة غير الأقرب انتهاءً مع ذكر السبب',
    descriptionEn: 'Pick a batch other than the first to expire, with a reason',
    offlineCapable: true,
  },
  {
    code: 'inventory.import',
    module: 'inventory',
    descriptionAr: 'الاستيراد من Excel',
    descriptionEn: 'Import from Excel',
    sensitive: true,
  },
  {
    code: 'inventory.opening.prepare',
    module: 'inventory',
    descriptionAr: 'إعداد مستندات الأرصدة الافتتاحية',
    descriptionEn: 'Prepare opening-balance documents',
    sensitive: true,
  },
  {
    code: 'inventory.correction.request',
    module: 'inventory',
    descriptionAr: 'طلب تصحيح مستند مُرحَّل (يوافق عليه شريك)',
    descriptionEn: 'Request the correction of a posted document (a partner approves)',
    sensitive: true,
  },
  // Suppliers and purchasing
  {
    code: 'supplier.view',
    module: 'purchasing',
    descriptionAr: 'عرض الموردين',
    descriptionEn: 'View suppliers',
  },
  {
    code: 'supplier.manage',
    module: 'purchasing',
    descriptionAr: 'إضافة وتعديل الموردين',
    descriptionEn: 'Add and edit suppliers',
  },
  {
    code: 'supplier.statement.view',
    module: 'purchasing',
    descriptionAr: 'عرض كشوف الموردين وأعمار الديون ومجموع المستحق',
    descriptionEn: 'View supplier statements, aging and the total owed',
  },
  {
    code: 'purchase.view',
    module: 'purchasing',
    descriptionAr: 'عرض فواتير الشراء بكمياتها وأسعارها',
    descriptionEn: 'View purchase invoices with quantities and prices',
  },
  {
    code: 'purchase.record',
    module: 'purchasing',
    descriptionAr: 'تسجيل فاتورة شراء مع صورتها',
    descriptionEn: 'Record a purchase invoice with its photo',
    offlineCapable: true,
  },
  {
    code: 'purchase.receive',
    module: 'purchasing',
    descriptionAr: 'استلام البضاعة وعدّها (استلام أعمى)',
    descriptionEn: 'Receive and count goods (blind receipt)',
    offlineCapable: true,
  },
  {
    code: 'purchase.discrepancy.resolve',
    module: 'purchasing',
    descriptionAr: 'البتّ في النقص والزيادة عند الاستلام',
    descriptionEn: 'Decide shortages and excess at receipt',
  },
  {
    code: 'supplier.payment.record',
    module: 'purchasing',
    descriptionAr: 'الدفع لمورد من صندوق في عهدته',
    descriptionEn: 'Pay a supplier from a cash box one holds',
    sensitive: true,
    offlineCapable: true,
  },
  {
    code: 'supplier.payment.approve',
    module: 'purchasing',
    descriptionAr: 'الموافقة على دفعات الموردين الكبيرة',
    descriptionEn: 'Approve large supplier payments',
    sensitive: true,
  },
  {
    code: 'supplier.return.record',
    module: 'purchasing',
    descriptionAr: 'إرجاع بضاعة إلى مورد',
    descriptionEn: 'Return goods to a supplier',
    offlineCapable: true,
  },
  // Production
  {
    code: 'production.recipe.manage',
    module: 'production',
    descriptionAr: 'قوالب الخلطات (الوصفات) ونُسخها',
    descriptionEn: 'Mix templates (recipes) and their versions',
  },
  {
    code: 'production.record',
    module: 'production',
    descriptionAr: 'تسجيل دفعة إنتاج (المكسر)',
    descriptionEn: 'Record a production batch (the mixer)',
    offlineCapable: true,
  },
  // Supplying the branches
  {
    code: 'supply.request.create',
    module: 'supply',
    descriptionAr: 'طلب بضاعة من المخزن للفرع',
    descriptionEn: 'Request goods from the warehouse for the branch',
    offlineCapable: true,
  },
  {
    code: 'supply.request.approve',
    module: 'supply',
    descriptionAr: 'الموافقة على طلبات الفروع أو تعديلها أو رفضها',
    descriptionEn: 'Approve, edit or reject branch requests',
    offlineCapable: true,
  },
  {
    code: 'supply.shipment.dispatch',
    module: 'supply',
    descriptionAr: 'تجهيز الشحنة وطباعة السند وإرسالها، والبيع المباشر',
    descriptionEn: 'Pick, print the note and dispatch a shipment; direct sale',
    offlineCapable: true,
  },
  {
    code: 'supply.shipment.receive',
    module: 'supply',
    descriptionAr: 'تأكيد استلام شحنة في الفرع',
    descriptionEn: 'Confirm a shipment at the branch',
    offlineCapable: true,
  },
  {
    code: 'supply.incident.resolve',
    module: 'supply',
    descriptionAr: 'اقتراح حلّ لفرق في شحنة',
    descriptionEn: 'Propose the outcome of a shipment difference',
  },
  {
    code: 'supply.incident.approve',
    module: 'supply',
    descriptionAr: 'الموافقة على شطب فرق ضاع في الطريق',
    descriptionEn: 'Approve writing off a difference lost on the way',
    sensitive: true,
  },
  {
    code: 'supply.account.view',
    module: 'supply',
    descriptionAr: 'عرض كشوف حسابات الفروع مع المخزن وأعمار ديونها',
    descriptionEn: "View branches' statements with the warehouse and their aging",
  },
  {
    code: 'supply.payment.receive',
    module: 'supply',
    descriptionAr: 'استلام دفعة فرع امتياز في صندوق في عهدته',
    descriptionEn: "Record a franchise's payment into a cash box one holds",
    sensitive: true,
    offlineCapable: true,
  },
  {
    code: 'supply.return.create',
    module: 'supply',
    descriptionAr: 'إرجاع بضاعة من الفرع إلى المخزن',
    descriptionEn: 'Return goods from the branch to the warehouse',
    offlineCapable: true,
  },
  // Counts
  {
    code: 'count.perform',
    module: 'counts',
    descriptionAr: 'الجرد (دون رؤية الكمية المتوقعة)',
    descriptionEn: 'Count stock (without seeing the expected quantity)',
    offlineCapable: true,
  },
  {
    code: 'count.approve',
    module: 'counts',
    descriptionAr: 'الموافقة على فروقات الجرد الكبيرة (ليس لجرده هو)',
    descriptionEn: 'Approve large count variances (never their own count)',
  },
  {
    code: 'count.configure',
    module: 'counts',
    descriptionAr: 'قوائم مواد الجرد',
    descriptionEn: 'Count lists',
  },
] as const satisfies readonly PermissionDefinition[];

export type PermissionCode = (typeof PERMISSIONS)[number]['code'];

export const PERMISSION_CODES: readonly PermissionCode[] = PERMISSIONS.map((p) => p.code);

/** Sensitive permissions that decide who may act on whom (D45): sensitive and not segregated. */
export const RANKING_PERMISSIONS: readonly PermissionCode[] = (
  PERMISSIONS as readonly PermissionDefinition[]
)
  .filter((p) => p.sensitive === true && p.segregated !== true)
  .map((p) => p.code as PermissionCode);
